import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAuthSidebar, useAuthTokens, UserType, useUser } from '@app/store/zustand';

import { type AuthenticateOptions } from './types';

export function useAuthentication() {
    const navigate = useNavigate();

    const {
        isAuthSidebarOpen,
        closeAuthSidebar,
        openAuthSidebar,
        postAuthRedirect,
        setPostAuthRedirect,
        removePostAuthRedirect,
    } = useAuthSidebar();
    const { isAuthenticated } = useAuthTokens();
    const { type: userType } = useUser();

    const [authCallback, setAuthCallback] = useState<null | (() => void)>(null);

    function protectedAction(protectedAction: () => void) {
        if (!isAuthenticated) {
            setAuthCallback(() => protectedAction);
            openAuthSidebar();
        } else {
            protectedAction();
        }
    }

    function authenticate(options?: AuthenticateOptions) {
        if (options?.flow) {
            localStorage.setItem('authFlow', options.flow);
        }

        if (options?.redirect) {
            setPostAuthRedirect(options.redirect);
        } else {
            removePostAuthRedirect();
        }
        openAuthSidebar();
    }

    /* Handle authentication via onClick event */
    function handleAuthenticate(event?: React.MouseEvent, options?: AuthenticateOptions) {
        event?.preventDefault();
        authenticate(options);
    }

    function protectedRedirect(link: string) {
        if (isAuthenticated) {
            navigate(link);
            return;
        }

        protectedAction(() => navigate(link));
    }

    useEffect(() => {
        if (isAuthenticated) {
            // User is verified, we can close the auth sidebar
            if (isAuthSidebarOpen && !postAuthRedirect && userType === UserType.Verified) {
                closeAuthSidebar();
            }

            if (authCallback) {
                authCallback();
                setAuthCallback(null);
            }
        }
    }, [authCallback, isAuthenticated, isAuthSidebarOpen, closeAuthSidebar, userType, postAuthRedirect]);

    return {
        authenticate,
        handleAuthenticate,
        protectedAction,
        protectedRedirect,
    };
}
